import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { User, Settings, LogOut, ChevronDown, Crown, Receipt } from "lucide-react";
import { useAuth } from "../../hooks/use-auth";
import ConfirmationModal from "./ConfirmationModal";

interface ProfileDropdownProps {
  className?: string;
}

const ProfileDropdown: React.FC<ProfileDropdownProps> = ({
  className = "",
}) => {
  const navigate = useNavigate();
  const { logout, role, plan } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Get user info from localStorage
  const userName =
    localStorage.getItem("userFullName") ||
    localStorage.getItem("userName") ||
    "User";
  const userEmail = localStorage.getItem("userEmail") || "";
  const businessName =
    localStorage.getItem("business_name") ||
    localStorage.getItem("restaurant_name") ||
    "";

  // Get initials for avatar
  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogoutClick = () => {
    setIsOpen(false);
    setShowLogoutModal(true);
  };

  const handleLogoutConfirm = () => {
    setShowLogoutModal(false);
    // Clear a flag to prevent "session expired" toast
    sessionStorage.setItem("manual_logout", "true");
    logout();
    navigate("/", { replace: true });
  };

  const handleProfileClick = () => {
    setIsOpen(false);
    navigate("/admin-profile");
  };

  const handleSettingsClick = () => {
    setIsOpen(false);
    navigate("/settings");
  };

  const handleBillingClick = () => {
    setIsOpen(false);
    navigate("/billing-history");
  };

  return (
    <>
      <div ref={dropdownRef} className={`relative ${className}`}>
        {/* Profile Button */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-2 p-1.5 rounded-full hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2"
        >
          {/* Avatar */}
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center text-white font-semibold text-sm shadow-md">
            {getInitials(userName)}
          </div>

          {/* Name & Role (hidden on mobile) */}
          <div className="hidden md:flex flex-col items-start">
            <span className="text-sm font-medium text-gray-800 leading-tight max-w-[120px] truncate">
              {userName}
            </span>
            <span className="text-xs text-gray-500 capitalize leading-tight">
              {role || "User"}
            </span>
          </div>

          {/* Chevron */}
          <ChevronDown
            size={16}
            className={`text-gray-500 transition-transform duration-200 ${
              isOpen ? "rotate-180" : ""
            }`}
          />
        </button>

        {/* Dropdown Menu */}
        {isOpen && (
          <div className="absolute right-0 top-full mt-2 w-64 bg-white rounded-xl shadow-xl border border-gray-100 py-2 z-[9998] animate-in fade-in slide-in-from-top-2 duration-200">
            {/* User Info Header */}
            <div className="px-4 py-3 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center text-white font-semibold">
                  {getInitials(userName)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">
                    {userName}
                  </p>
                  <p className="text-xs text-gray-500 truncate">{userEmail}</p>
                </div>
              </div>
              {businessName && (
                <div className="mt-2 flex items-center gap-1.5 text-xs text-gray-600 bg-gray-50 px-2 py-1 rounded">
                  <span className="truncate">{businessName}</span>
                </div>
              )}
              {plan && (
                <div className="mt-2 flex items-center gap-1.5 text-xs text-orange-600 bg-orange-50 px-2 py-1 rounded">
                  <Crown size={12} />
                  <span className="capitalize">{plan}</span>
                </div>
              )}
            </div>

            {/* Menu Items */}
            <div className="py-1">
              <button
                onClick={handleProfileClick}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <User size={16} className="text-gray-500" />
                <span>My Profile</span>
              </button>

              <button
                onClick={handleBillingClick}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <Receipt size={16} className="text-gray-500" />
                <span>Billing History</span>
              </button>

              <button
                onClick={handleSettingsClick}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <Settings size={16} className="text-gray-500" />
                <span>Settings</span>
              </button>
            </div>

            {/* Logout */}
            <div className="border-t border-gray-100 pt-1">
              <button
                onClick={handleLogoutClick}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
              >
                <LogOut size={16} />
                <span>Logout</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Logout Confirmation Modal */}
      <ConfirmationModal
        isOpen={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        onConfirm={handleLogoutConfirm}
        type="logout"
        title="Logout"
        message="Are you sure you want to logout? You'll need to login again to access your account."
        confirmText="Logout"
        cancelText="Cancel"
      />
    </>
  );
};

export default ProfileDropdown;
